// 结算弹窗商品明细的展示行构建（纯函数，便于单测）：
// 卡面条目 + 增值权益条目 → 统一展示行 { key, imageUrl?, name, detail?, price(元) }。
// 卡面条目单价取 ecard_goods 商品目录价（元，与试算/下单定价同源的权威口径），
// 不用条目自带 price（购物车条目是分、有赞 SKU 是元，两个入口单位不一致）。
// 增值权益价格本身即元，直接透传。

import { typeLabel } from '../../pages/BuyCards/cardOptionUtils'

// 增值权益标题 → 展示名 i18n key：结算弹窗明细与加购卡片区（ValueAddedServices）
// 共用同一套文案，由 React-Intl 按语言渲染；未知标题返回 null，调用方原样展示后端 title
const VALUE_ADDED_TITLE_NAME_IDS = {
  实体卡85折: 'ecard-value-added-card-discount',
  周边8折: 'ecard-value-added-around-discount',
}

export const getValueAddedNameId = (title) => VALUE_ADDED_TITLE_NAME_IDS[title] || null

/**
 * 构建结算弹窗商品明细行列表。
 * 条目双形态兼容（购物车响应契约改造 2026-09-02）：
 * slim（服务端购物车：itemNo/title/type/picUrl）+ rich（BuyCards 本地：skuId/styleName/typeName/imageUrl）。
 *
 * @param {Array} items 卡面条目
 * @param {Array} valueAdded 增值权益条目（{ title, type, price(元) }）
 * @param {Array} goodsCatalog ecard_goods 目录（{ itemNo, price(元) }），按 itemNo 取权威单价
 * @returns {Array<{key: string, imageUrl?: string, name: string, nameId?: string, detail?: string, price: number|null}>}
 *          price 为 null 表示目录未匹配（目录未加载/卡面未配置），调用方展示占位；
 *          nameId 非空时名称走 i18n（formatMessage），name 为未知标题的原始回退
 */
export const buildOrderItemList = (items, valueAdded, goodsCatalog) => {
  const catalog = goodsCatalog || []
  const cardRows = (items || []).map((item) => {
    const goods = catalog.find((g) => g.itemNo === item.itemNo)
    const styleName = item.title || item.styleName
    return {
      // 条目互斥键：slim 用 itemNo、rich 用 skuId（两形态各自稳定唯一）
      key: `${item.itemNo ?? item.skuId}-${item.ecardId}`,
      itemNo: item.itemNo,
      // slim 条目自带目录图，rich 条目透传有赞 SKU 图
      imageUrl: item.picUrl || item.imageUrl,
      // 名称对齐购物车条目展示：《卡面名》；无卡面名（异常数据）时回退证书名
      name: styleName ? `《${styleName}》` : item.ecardName,
      // 规格行对齐购物车条目展示：卡类型/证书名；名称已含证书名时不重复展示
      detail: [item.typeName || typeLabel(item.type), styleName ? item.ecardName : null].filter(Boolean).join('/'),
      price: goods ? Number(goods.price) || 0 : null,
    }
  })
  const addedRows = (valueAdded || []).map((v) => ({
    key: v.type,
    // name 为未知标题的原始回退；已知标题走 nameId（i18n）
    name: v.title,
    nameId: getValueAddedNameId(v.title),
    price: Number(v.price) || 0,
  }))
  return [...cardRows, ...addedRows]
}

/**
 * 按权威折扣总额（试算 couponPrice）给实体卡条目分摊折后价：
 * 优惠只作用于实体卡（STANDARD_PHYSICAL，MEMBER_CARD 已退役，与券适用条件一致），
 * 各行按目录价占比分摊折扣，折后价之和与 itemsPrice − couponPrice（页脚权威金额）
 * 一致（逐行四舍五入误差 ≤1 分）。券折扣率为 0（club 0元券）等后端特殊限额场景，
 * 总额仍与页脚一致（分摊近似逐条明细）。
 *
 * @param {Array} rows buildOrderItemList 的返回行
 * @param {Set<string>} physicalItemNos 实体卡 itemNo 集合
 * @param {string|number} couponPrice 试算返回的权威折扣总额（元）
 * @returns {Array} 各行增加 discountedPrice（元，两位小数）；非实体卡/无折扣时原样返回
 */
export const applyCouponDiscount = (rows, physicalItemNos, couponPrice) => {
  const discount = Number(couponPrice) || 0
  if (discount <= 0 || !rows || rows.length === 0) return rows || []
  const discountable = rows.filter(
    (row) => row.itemNo && physicalItemNos && physicalItemNos.has(row.itemNo) && row.price != null
  )
  const base = discountable.reduce((sum, row) => sum + row.price, 0)
  if (base <= 0) return rows
  return rows.map((row) => {
    if (!row.itemNo || !physicalItemNos || !physicalItemNos.has(row.itemNo) || row.price == null) return row
    // 下限 clamp 到 0：couponPrice 超过实体行目录价总和（混合订单券还折了非实体行等
    // 异常口径）时，不允许渲染负折后价，页脚权威金额不受影响
    const discounted = Math.round((row.price - (discount * row.price) / base) * 100) / 100
    return { ...row, discountedPrice: Math.max(0, discounted) }
  })
}
